/* selection-prompt.js — build a chat prompt from an editor selection.
 *
 * Pure. Lets a dev Explain / Fix / Send selected code to the agent without
 * copy-pasting. The result is prefilled into the composer (the dev can edit
 * before sending — we never auto-send).
 */

/**
 * @param {'explain'|'fix'|'send'} verb
 * @param {{text:string, name:string, language?:string, startLine:number, endLine:number}} ctx
 * @returns {string}
 */
export function buildSelectionPrompt(verb, ctx) {
  const where =
    ctx.startLine === ctx.endLine
      ? `line ${ctx.startLine} of ${ctx.name}`
      : `lines ${ctx.startLine}-${ctx.endLine} of ${ctx.name}`;
  const lead =
    verb === "fix"
      ? "Fix this code"
      : verb === "explain"
        ? "Explain this code"
        : "Here is some code for context";
  const lang = ctx.language || "";
  return `${lead} (${where}):\n\`\`\`${lang}\n${ctx.text}\n\`\`\`\n`;
}

/**
 * Build an Explain prompt for the inline assistant drawer (Code view's
 * selection toolbar / whole-file Ask AI button). Plain text — no esc() here,
 * this becomes ctx.prompt, sent verbatim via sendComposerText().
 *
 * @param {{text:string, name:string, language?:string, startLine:number, endLine:number}} sel
 * @returns {string}
 */
export function buildExplainPrompt(sel) {
  const where = sel.startLine === sel.endLine ? `line ${sel.startLine}` : `lines ${sel.startLine}-${sel.endLine}`;
  return `Explain this code (${where} of ${sel.name}). Be concise and reference line numbers.\n\n` +
    `\`\`\`${sel.language || "python"}\n${sel.text}\n\`\`\`\n`;
}

/**
 * Build an Improve prompt for the inline assistant drawer. Deliberately
 * scoped to exactly the selected fragment — the drawer only ever renders the
 * streamed answer (the Improve diff card that lets a dev apply it is a
 * separate, later piece of work), so the prompt itself is what keeps the
 * model from rewriting the whole file or claiming to have applied anything.
 *
 * @param {{text:string, name:string, language?:string, startLine:number, endLine:number}} sel
 * @returns {string}
 */
export function buildImprovePrompt(sel) {
  const where = sel.startLine === sel.endLine ? `line ${sel.startLine}` : `lines ${sel.startLine}-${sel.endLine}`;
  return `Improve this code fragment (${where} of ${sel.name}).\n\n` +
    `\`\`\`${sel.language || "python"}\n${sel.text}\n\`\`\`\n\n` +
    `Reply with (1) the improved version of exactly this fragment in ONE fenced ` +
    `${sel.language || "ironpython"} block, then (2) a short "Why" list. ` +
    `Do not modify anything outside the fragment. Do not apply changes.\n`;
}

/**
 * Build a prompt for the Database view's "Ask about this table" affordance.
 * Marks SAP-platform (non-custom) tables read-only so the model doesn't
 * propose writes to a table the agent can't (and shouldn't) modify.
 *
 * @param {string} question
 * @param {{name:string, isCustom?:boolean, columns?:string[]}} table
 * @returns {string}
 */
export function buildTablePrompt(question, table) {
  const cols = (table.columns || []).slice(0, 40).join(", ");
  const kind = table.isCustom ? "a custom table" : "a read-only platform table";
  return `Database table ${table.name} (${kind}). Columns: ${cols}.\n\n${question}\n`;
}

/**
 * Build a prompt for the Audit view's "Ask AI" affordance on one journal
 * entry. Synchronous -- the entry (including its already-fetched unified
 * diff, if any) is passed in whole; this never fetches anything itself, so a
 * chip click can build and send the prompt in the same tick.
 *
 * The diff excerpt is capped at 2000 characters so a large before/after pair
 * can't blow out the prompt budget -- plenty to show the shape of a change
 * without embedding an entire file rewrite.
 *
 * Plain text -- no esc() here; this lands in the composer textarea, not in HTML.
 *
 * @param {string} question
 * @param {{op?:string, family?:string, targetLabel?:string, tool?:string, ts?:string, reversibility?:string, reverse_status?:string, diff?:string}} entry
 * @returns {string}
 */
export function buildAuditPrompt(question, entry) {
  const e = entry || {};
  const meta = [
    `Change: ${e.op || "?"} ${e.targetLabel || e.family || ""}`.trim(),
    `Tool: ${e.tool || "?"}`,
    `When: ${e.ts || "?"}`,
    `Reversibility: ${e.reversibility || "?"}`,
    (e.reverse_status && e.reverse_status !== "pending") ? `Status: ${e.reverse_status}` : "",
  ].filter(Boolean).join("\n");
  const diff = e.diff ? `\n\nDiff excerpt:\n\`\`\`diff\n${String(e.diff).slice(0, 2000)}\n\`\`\`` : "";
  return `About this audited change:\n${meta}${diff}\n\n${question}\n`;
}

/**
 * Build a consolidated plain-text prompt asking the agent to fix ALL failed/blocked
 * promotion assets in one shot and prepare the target tenant for a retry.
 *
 * Plain text — no esc() here; this lands in the composer textarea, not in HTML.
 *
 * @param {{assets: Array<{type:string, name:string, error?:string}>, source:string, target:string}} opts
 * @returns {string}
 */
export function buildPromotionFixAllPrompt({ assets = [], source = "", target = "" } = {}) {
  const rows = assets
    .map(a => `- ${a.type} "${a.name}": ${a.error || "(no error message)"}`)
    .join("\n");
  return [
    `A CPQ promotion from ${source} to ${target} had ${assets.length} failed/blocked asset(s):`,
    rows,
    ``,
    `Diagnose the ROOT CAUSE of each failure on the target tenant (${target}) and apply a surgical fix`,
    `(e.g. create a missing referenced object, repoint an autocomplete value-code column, enable a`,
    `prerequisite application parameter). Change only what's required. After you finish, the promotion`,
    `will be retried for these assets — do not re-run the promotion yourself.`,
  ].join("\n");
}

/**
 * Build a plain-text prompt for the composer when the user asks the agent to
 * investigate a failed or blocked promotion step.
 *
 * Plain text — no esc() here; this lands in the composer textarea, not in HTML.
 *
 * @param {{type:string, name:string, error:string, source:string, target:string}} opts
 * @returns {string}
 */
export function buildPromotionFixPrompt({ type, name, error, source, target } = {}) {
  const lines = [
    `A CPQ promotion step failed and I need help fixing it.`,
    ``,
    `- Asset type: ${type || "(unknown)"}`,
    `- Asset name: ${name || "(unknown)"}`,
    `- Promoting from: ${source || "(unknown)"} → ${target || "(unknown)"}`,
    ``,
    `Error:`,
    "```",
    String(error || "(no error message captured)"),
    "```",
    ``,
    `Investigate this asset and the error, explain the root cause, and propose a concrete fix so the promotion can be retried.`,
  ];
  return lines.join("\n");
}
